import { useEffect, useId, useMemo, useRef, useState } from 'react';

export interface SearchSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Nota curta à direita da opção — "ciclo atual", "sem conta nesta operadora". */
  hint?: string;
}

interface SearchSelectCommonProps {
  options: SearchSelectOption[];
  /** O que o campo mostra sem valor — também é o "todos" dos filtros. */
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  /** Mostra o × que volta ao vazio — para filtros cujo vazio significa "todos". */
  clearable?: boolean;
  /** `md` = campo de formulário; `sm` = filtro compacto de toolbar. */
  size?: 'md' | 'sm';
  /** `pill` tira borda e fundo para viver dentro de um rótulo arredondado. */
  variant?: 'field' | 'pill';
  'aria-label'?: string;
  className?: string;
  /** Classe extra do painel — largura, por exemplo, para rótulos longos. */
  panelClassName?: string;
  /** Deixa o rótulo da opção quebrar em linhas em vez de cortar com reticências. */
  wrapOptions?: boolean;
}

/** Modo único, o dos selects de formulário: um valor, fecha ao escolher. */
interface SearchSelectSingleProps extends SearchSelectCommonProps {
  multiple?: false;
  /** `''` = nada selecionado (o papel da `<option value="">` de antes). */
  value: string;
  onChange: (value: string) => void;
}

/** Modo múltiplo: alterna valores sem fechar, com selecionar/limpar tudo. */
interface SearchSelectMultipleProps extends SearchSelectCommonProps {
  multiple: true;
  values: string[];
  onValuesChange: (values: string[]) => void;
}

type SearchSelectProps = SearchSelectSingleProps | SearchSelectMultipleProps;

/**
 * Minúsculas e sem acento, código unit a código unit, para a busca achar
 * "Goiania" em "Goiânia". Por código unit (e não na string inteira) para o
 * texto dobrado ter o mesmo comprimento do original — é o que deixa o trecho
 * encontrado ser destacado por índice sem desalinhar.
 */
function fold(text: string): string {
  return text
    .split('')
    .map((char) => {
      const folded = char.normalize('NFD').replace(/[̀-ͯ]/g, '');
      return (folded.length === 1 ? folded : char).toLowerCase();
    })
    .join('');
}

/** O rótulo com o trecho buscado marcado; `fold` garante os índices. */
function Highlight({ label, query }: { label: string; query: string }) {
  const at = query ? fold(label).indexOf(fold(query)) : -1;

  if (at < 0) return <>{label}</>;

  return (
    <>
      {label.slice(0, at)}
      <mark className="rounded-[3px] bg-[#0024471f] text-inherit">
        {label.slice(at, at + query.length)}
      </mark>
      {label.slice(at + query.length)}
    </>
  );
}

/**
 * Select com busca — o substituto dos `<select>` nativos do pacote.
 *
 * Gatilho estilizado como os campos do sistema; o painel abre com a busca já
 * focada, filtra sem distinguir acento e navega pelo teclado (setas, Enter,
 * Esc). No modo único o valor é controlado como no nativo: string, `''` para
 * vazio. Com `multiple`, as opções viram caixas de seleção, escolher não fecha
 * o painel e as ações "Selecionar tudo" / "Limpar tudo" ficam acima da lista.
 */
export function SearchSelect(props: SearchSelectProps) {
  const {
    options,
    placeholder = 'Selecione…',
    searchPlaceholder = 'Buscar…',
    disabled = false,
    clearable = false,
    size = 'md',
    variant = 'field',
    'aria-label': ariaLabel,
    className = '',
    panelClassName = '',
    wrapOptions = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropUp, setDropUp] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  // Os dois modos convergem para uma lista de valores: no único ela tem no
  // máximo um item, e todo o resto do componente enxerga só a lista.
  const selectedValues = props.multiple ? props.values : props.value ? [props.value] : [];
  const isSelected = (value: string) => selectedValues.includes(value);
  const selectedOptions = options.filter((option) => isSelected(option.value));

  const visible = useMemo(() => {
    const needle = fold(query.trim());
    if (!needle) return options;
    return options.filter((option) => fold(option.label).includes(needle));
  }, [options, query]);

  const openPanel = () => {
    if (disabled) return;
    setQuery('');
    setDropUp(false);
    setActiveIndex(Math.max(0, options.findIndex((option) => isSelected(option.value))));
    setOpen(true);
  };

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  /** Escolha de uma opção: única fecha e troca; múltipla alterna e fica aberta. */
  const choose = (value: string) => {
    if (props.multiple) {
      props.onValuesChange(
        isSelected(value)
          ? props.values.filter((candidate) => candidate !== value)
          : [...props.values, value],
      );
      return;
    }

    close(true);
    if (value !== props.value) props.onChange(value);
  };

  /** O × do gatilho: volta ao vazio nos dois modos. */
  const clear = () => {
    close(true);
    if (props.multiple) {
      if (props.values.length) props.onValuesChange([]);
    } else if (props.value !== '') {
      props.onChange('');
    }
  };

  /**
   * "Selecionar tudo" vale para o que a busca está mostrando (sem as
   * desabilitadas): com o filtro ativo é isso que a pessoa está vendo, e somar
   * ao que já estava escolhido preserva as seleções fora do filtro.
   */
  const selectAllVisible = () => {
    if (!props.multiple) return;

    const additions = visible
      .filter((option) => !option.disabled && !isSelected(option.value))
      .map((option) => option.value);

    if (additions.length) props.onValuesChange([...props.values, ...additions]);
  };

  const clearAll = () => {
    if (props.multiple && props.values.length) props.onValuesChange([]);
  };

  // Com o painel aberto: foca a busca, decide se abre para cima (quando não
  // cabe abaixo) e fecha no clique fora.
  useEffect(() => {
    if (!open) return;

    searchRef.current?.focus();

    const panel = panelRef.current;
    if (panel) {
      const rect = panel.getBoundingClientRect();
      if (rect.bottom > window.innerHeight - 12 && rect.top > rect.height + 24) {
        setDropUp(true);
      }
    }

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  // A opção ativa acompanha o teclado mesmo com a lista rolada.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(`${listId}-opt-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, listId]);

  const move = (direction: 1 | -1) => {
    if (!visible.length) return;

    let index = activeIndex;
    for (let step = 0; step < visible.length; step++) {
      index = (index + direction + visible.length) % visible.length;
      if (!visible[index].disabled) break;
    }
    setActiveIndex(index);
  };

  const onSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = visible[activeIndex];
      if (option && !option.disabled) choose(option.value);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  const pill = variant === 'pill';

  // O resumo do gatilho: um escolhido mostra o rótulo; mais de um, a contagem —
  // concatenar rótulos estouraria o campo com três masters escolhidos.
  const triggerLabel =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} selecionados`;

  const hasValue = selectedOptions.length > 0;

  const triggerClass = pill
    ? 'text-navy-800 flex cursor-pointer items-center gap-1.5 rounded-md text-[12.5px] font-bold outline-none focus-visible:ring-2 focus-visible:ring-[#00244733]'
    : [
        'flex w-full items-center gap-2 rounded-[10px] border text-left outline-none',
        size === 'sm' ? 'min-h-9 px-2.5 py-1.5 text-[12.5px] font-semibold' : 'px-3 py-[11px] text-[14px]',
        disabled
          ? 'border-line-300 text-ink-400 cursor-not-allowed bg-[#F7F9FC]'
          : open
            ? 'border-navy-800 text-ink-900 bg-white ring-2 ring-[#00244733]'
            : 'border-line-300 focus:border-navy-800 text-ink-900 bg-white focus:ring-2 focus:ring-[#00244733]',
      ].join(' ');

  // Quem passa a própria largura (w-, min-w-, flex-) não quer o w-full padrão
  // junto — duas classes de largura deixam o resultado por conta da ordem do
  // stylesheet.
  const hasExplicitWidth = /(^|\s)(w-|min-w-|max-w-|flex-)/.test(className);

  const rootClass = [
    'relative',
    pill || size === 'sm' ? 'inline-block' : hasExplicitWidth ? '' : 'w-full',
    size === 'sm' && !pill ? 'min-w-[190px]' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={rootRef} className={rootClass}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? close(true) : openPanel())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            openPanel();
          }
        }}
        className={triggerClass}
      >
        <span
          className={`min-w-0 flex-1 truncate ${hasValue ? '' : pill ? 'text-ink-400' : 'text-ink-400 font-normal'}`}
        >
          {triggerLabel}
        </span>

        {clearable && hasValue && !disabled ? (
          // span, e não button: button dentro de button é inválido e o parser
          // de HTML desmonta a árvore. stopPropagation para o clique não
          // alternar o painel; preventDefault para o <label> das telas não
          // reenviar o clique ao gatilho.
          <span
            role="button"
            tabIndex={-1}
            aria-label="Limpar seleção"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              clear();
            }}
            className="text-ink-400 hover:text-ink-900 -m-0.5 rounded p-0.5"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </span>
        ) : null}

        <span
          className={`text-ink-400 flex-none transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path
              d="M3.5 6l4.5 4.5L12.5 6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <div
          ref={panelRef}
          // As telas embrulham o campo num <label>; sem o preventDefault, o
          // clique numa opção seria reenviado pelo label ao gatilho e o painel
          // reabriria logo depois de fechar.
          onClick={(event) => event.preventDefault()}
          className={[
            'border-line-300 absolute z-30 overflow-hidden rounded-xl border bg-white shadow-[0_10px_30px_rgba(19,31,56,0.14)]',
            dropUp ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]',
            pill ? 'left-1/2 w-[260px] -translate-x-1/2' : 'left-0 right-0 min-w-[230px]',
            panelClassName,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="border-line-200 text-ink-400 flex items-center gap-2 border-b px-3 py-2">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.6" />
              <path d="M10.6 10.6L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onSearchKeyDown}
              placeholder={searchPlaceholder}
              aria-label="Buscar opção"
              className="text-ink-900 placeholder:text-ink-400 w-full min-w-0 flex-1 bg-transparent text-[13.5px] font-normal outline-none"
            />
          </div>

          {props.multiple ? (
            <div className="border-line-200 flex items-center justify-between border-b px-3 py-1.5 text-[11px] font-bold">
              {/* mousedown não tira o foco da busca; o clique ainda age. */}
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={selectAllVisible}
                className="text-navy-800 hover:underline"
              >
                Selecionar tudo
              </button>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={clearAll}
                className="text-ink-400 hover:text-ink-900"
              >
                Limpar tudo
              </button>
            </div>
          ) : null}

          <ul
            id={listId}
            role="listbox"
            aria-multiselectable={props.multiple || undefined}
            className="m-0 max-h-[248px] list-none overflow-y-auto p-[5px]"
          >
            {visible.length === 0 ? (
              <li className="text-ink-400 px-3 py-4 text-center text-[13px] font-normal">
                Nenhum resultado para “{query.trim()}”
              </li>
            ) : null}

            {visible.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-opt-${index}`}
                role="option"
                aria-selected={isSelected(option.value)}
                aria-disabled={option.disabled || undefined}
                // mousedown roubaria o foco da busca antes do clique completar.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (!option.disabled) choose(option.value);
                }}
                onMouseMove={() => {
                  if (!option.disabled && activeIndex !== index) setActiveIndex(index);
                }}
                className={[
                  'flex items-center gap-2 rounded-lg px-2 py-2 text-[13.5px]',
                  option.disabled
                    ? 'text-ink-400 cursor-not-allowed font-normal'
                    : isSelected(option.value)
                      ? 'text-navy-800 cursor-pointer font-extrabold'
                      : 'text-ink-900 cursor-pointer font-normal',
                  index === activeIndex && !option.disabled ? 'bg-[#E3ECF9]' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {props.multiple ? (
                  <span
                    className={`flex h-[15px] w-[15px] flex-none items-center justify-center rounded-[4px] border ${
                      isSelected(option.value)
                        ? 'border-navy-800 bg-navy-800 text-white'
                        : 'border-line-300 bg-white text-transparent'
                    }`}
                    aria-hidden="true"
                  >
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8.5l3.2 3L13 5"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                ) : (
                  <span
                    className={`text-navy-800 flex-none ${isSelected(option.value) ? '' : 'invisible'}`}
                    aria-hidden="true"
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8.5l3.2 3L13 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}

                <span
                  className={`min-w-0 flex-1 ${wrapOptions ? 'break-words whitespace-normal' : 'truncate'}`}
                >
                  <Highlight label={option.label} query={query.trim()} />
                </span>

                {option.hint ? (
                  <span className="text-ink-400 flex-none text-[11.5px] font-bold">{option.hint}</span>
                ) : null}
              </li>
            ))}
          </ul>

          <div className="border-line-200 text-ink-400 border-t px-3 py-1.5 text-[11px] font-bold">
            {visible.length} de {options.length} {options.length === 1 ? 'opção' : 'opções'}
            {props.multiple && selectedValues.length
              ? ` · ${selectedValues.length} selecionada${selectedValues.length === 1 ? '' : 's'}`
              : ''}
          </div>
        </div>
      ) : null}
    </div>
  );
}
